import clsx from 'clsx'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import React, { useCallback, useMemo } from 'react'
import styled, { keyframes } from 'styled-components'
import { nonNullable, unique } from 'lib/array/arrayUtils'
import { stepImageIds } from 'tripPlanner/pages/trip/AITripConstants'
import { AITripPreferenceStep } from 'tripPlanner/pages/trip/GenerateTripPage'
import { FullTrip } from 'tripPlanner/types/common'

const fadeAnimation = keyframes`
  0% { 
    opacity: 1; 
    transform: scale(1);
  }
  15% { 
    opacity: 1;
    transform: scale(1.05);
  }
  17% { 
    opacity: 0.5;
    transform: scale(1.05);
  }
  19% { 
    opacity: 0;
    transform: scale(1.05);
  }
  80% { 
    opacity: 0;
    transform: scale(1);
  }
  85% { 
    opacity: 1;
    transform: scale(1);
  }
  100% { 
    opacity: 1;
    transform: scale(1);
  }
`

const fadeAnimation2 = keyframes`
  0% { 
    opacity: 0;
    transform: scale(1);
  }
  15% { 
    opacity: 0;
    transform: scale(1);
  }
  16% { 
    opacity: 0.2;
    transform: scale(1);
  }
  20% { 
    opacity: 1;
    transform: scale(1.01);
  }
  35% { 
    opacity: 1;
    transform: scale(1.05);
  }
  37% { 
    opacity: 0.5;
    transform: scale(1.05);
  }
  39% { 
    opacity: 0;
    transform: scale(1.05);
  }
  100% { 
    opacity: 0;
    transform: scale(1);
  }
`

const fadeAnimation3 = keyframes`
  0% { 
    opacity: 0;
    transform: scale(1);
  }
  35% { 
    opacity: 0;
    transform: scale(1);
  }
  36% { 
    opacity: 0.2;
    transform: scale(1);
  }
  40% { 
    opacity: 1;
    transform: scale(1.01);
  }
  55% { 
    opacity: 1; 
    transform: scale(1.05);
  }
  57% { 
    opacity: 0.5; 
    transform: scale(1.05);
  }
  59% { 
    opacity: 0; 
    transform: scale(1.05);
  }
  100% { 
    opacity: 0;
    transform: scale(1);
  }
`

const fadeAnimation4 = keyframes`
  0% { 
    opacity: 0;
    transform: scale(1);
  }
  55% { 
    opacity: 0;
    transform: scale(1);
  }
  56% { 
    opacity: 0.2;
    transform: scale(1);
  }
  60% { 
    opacity: 1;
    transform: scale(1.01);
  }
  75% { 
    opacity: 1; 
    transform: scale(1.05);
  }
  77% { 
    opacity: 0.5; 
    transform: scale(1.05);
  }
  79% { 
    opacity: 0; 
    transform: scale(1.05);
  }
  100% { 
    opacity: 0;
    transform: scale(1);
  }
`

const fadeAnimation5 = keyframes`
  0% { 
    opacity: 0;
    transform: scale(1);
  }
  75% { 
    opacity: 0;
    transform: scale(1);
  }
  76% { 
    opacity: 0.2;
    transform: scale(1);
  }
  80% { 
    opacity: 1;
    transform: scale(1.01);
  }
  95% { 
    opacity: 1;
    transform: scale(1.05);
  }
  97% { 
    opacity: 0.5;
    transform: scale(1.05);
  }
  99% { 
    opacity: 0;
    transform: scale(1.05);
  }
  100% { 
    opacity: 0;
    transform: scale(1.05);
  }
`

const ImageContainer = styled.div`
  grid-area: image;
  display: none;
  position: relative;
  overflow: hidden;

  ${mediaQueryUp.desktop} {
    display: grid;
    grid-template: minmax(0, 1fr) / minmax(0, 1fr);

    max-height: 100vh;

    > * {
      grid-column: 1 / -1;
      grid-row: 1 / -1;
    }
  }
`

const FadingResponsiveImage = styled(ResponsiveImage)`
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  
  &.is-visible:not(.is-generating) {
    opacity: 1;
  }
  
  &.is-generating {
    &.image-0 {
      animation: ${fadeAnimation} 20s linear infinite;
      animation-fill-mode: both;
    }
    &.image-1 {
      animation: ${fadeAnimation2} 20s linear infinite;
      animation-fill-mode: both;
    }
    &.image-2 {
      animation: ${fadeAnimation3} 20s linear infinite;
      animation-fill-mode: both;
    }
    &.image-3 {
      animation: ${fadeAnimation4} 20s linear infinite;
      animation-fill-mode: both;
    }
    &.image-4 {
      animation: ${fadeAnimation5} 20s linear infinite;
      animation-fill-mode: both;
    }
  }
`

interface Props {
  step: AITripPreferenceStep
  trip: FullTrip | undefined
  locationImageIds: Array<string>
  isLocationDataLoading?: boolean
}

const STEP_IMAGES = nonNullable(Object.values(stepImageIds))

function AITripImageDisplay({ step, trip, locationImageIds, isLocationDataLoading = false }: Props) {
  const allPossibleImages = useMemo((): Array<string> => {
    return unique([...STEP_IMAGES, ...locationImageIds])
  }, [locationImageIds])

  const shouldShowImage = useCallback((imageId: string) => {
    if (isLocationDataLoading) {
      return false
    }

    const isGeneratingTrip = trip?.generationStatus === 'PENDING'
    const hasNoGenerationStatus = !trip?.generationStatus
    const defaultImage = locationImageIds[0]
    const stepImageId = hasNoGenerationStatus ? stepImageIds[step] : undefined
    const targetImageId = stepImageId || defaultImage

    return isGeneratingTrip ?
        locationImageIds.includes(imageId) :
      imageId === targetImageId
  }, [trip?.generationStatus, step, locationImageIds, isLocationDataLoading])

  const isGenerating = trip?.generationStatus === 'PENDING'

  return (
    <ImageContainer>
      {allPossibleImages.map((id) => (
        <FadingResponsiveImage
          key={id}
          id={id}
          fit="center"
          quality="eco"
          dpr={1}
          tabletWidth="50vw"
          className={clsx(`image-${isGenerating ? locationImageIds.indexOf(id) : 0}`, {
            'is-generating': isGenerating && shouldShowImage(id),
            'is-visible': shouldShowImage(id),
          })}
        />
      ))}
    </ImageContainer>
  )
}

export default AITripImageDisplay
